import React, { FC, PropsWithChildren, useState } from "react";
import { TextInput } from "react-native";
import { Button } from "@lookiero/sty-psp-ui";

enum PromoType {
  URL,
  Site,
  Manual,
  MultiBox,
}

interface PromoParam {
  readonly value: string;
  readonly type?: PromoType;
}

enum ROUTE_KEY {
  SHOP = "SHOP",
}

interface LocaliseParam {
  readonly value: string;
  readonly link?: ROUTE_KEY;
}

type LocaliseParams = Record<string, LocaliseParam>;

interface Localise {
  readonly id: string;
  readonly params?: LocaliseParams;
}

interface PromoCodeError {
  readonly promoCode: PromoParam;
  readonly rawError: Error;
  readonly errorCode?: string;
  readonly errorMessage?: string;
  readonly localise?: Localise;
}

type OrderId = number;

enum UserSubscription {
  OCCASIONAL = "0",
  MONTHLY = "1",
  BIMONTHLY = "2",
  QUARTERLY = "3",
}

interface PromoCode {
  readonly name: string;
  readonly id?: number;
  readonly discountAmount?: number;
  readonly discountPct?: number;
  readonly discountServicePct?: number;
  readonly remainingApplicableOrders?: number;
  readonly applicableOrders?: number;
  readonly device?: string;
  readonly service?: string;
  readonly type?: PromoType;
  readonly localise?: Localise;
  readonly isFirstOrder?: boolean;
  readonly orderId?: OrderId;
  readonly userSubscription?: UserSubscription;
}

type FakePromocodeOutcome =
  | { readonly success: Omit<PromoCode, "name"> }
  | { readonly error: Omit<PromoCodeError, "promoCode" | "rawError"> };

interface PromoComponentProps {
  readonly promoCode?: { readonly value: string | null };
  readonly service: SERVICE;
  readonly hideButtonOnEmpty?: boolean;
  readonly showLink?: boolean;
  readonly onValidate: (response: PromoCode) => void;
  readonly onError?: (error: PromoCodeError) => void;
}

const FAKE_PROMOCODE_OUTCOME_KEY = "__lookInFakePromocodeOutcome";

const fakePromocodeOutcome = (): FakePromocodeOutcome | undefined =>
  (globalThis as Record<string, unknown>)[FAKE_PROMOCODE_OUTCOME_KEY] as FakePromocodeOutcome | undefined;

const setPromocodesBridge = () => void 0;
const PromoCodesProvider: FC<PropsWithChildren> = ({ children }) => children;

const FakePromoInput: FC<PromoComponentProps> = ({ hideButtonOnEmpty, onValidate, onError }) => {
  const [value, setValue] = useState("");

  const handleOnApply = () => {
    const outcome = fakePromocodeOutcome();

    if (!outcome) {
      return;
    }

    if ("success" in outcome) {
      onValidate({ ...outcome.success, name: value });
    } else {
      onError?.({ ...outcome.error, promoCode: { value, type: PromoType.Manual }, rawError: new Error() });
    }
  };

  return (
    <>
      <TextInput testID="promocode-input" value={value} onChangeText={setValue} />
      {hideButtonOnEmpty && !value ? null : (
        <Button testID="validate-promocode-button" onPress={handleOnApply}>
          Apply
        </Button>
      )}
    </>
  );
};

const PromoComponent: FC<PromoComponentProps> = (props) =>
  fakePromocodeOutcome() ? <FakePromoInput {...props} /> : null;

enum SERVICE {
  BOX = "box",
  SHOP = "shop",
}

export type { PromoParam, PromoCodeError, PromoCode };
export { setPromocodesBridge, PromoCodesProvider, PromoComponent, SERVICE };
